"use client";

import { useState, useEffect, useRef } from "react";
import JSZip from "jszip";
import posthog from "posthog-js";
import {
  SLIDE_W, SLIDE_H,
  type CoverSlideData, type SpeciesSlideData, type ClosingSlideData, type SlideStyle,
} from "@/lib/carousel/types";
import { canvasToPngBlob } from "@/lib/carousel/shared";
import { FONT_PAIRINGS, DEFAULT_PAIRING, ensureFontsLoaded } from "@/lib/carousel/fonts";
import { TEMPLATES, DEFAULT_TEMPLATE } from "@/lib/carousel/templates";
import { assignNameSlots, detectDefaultNameOrder, saveNameOrderPreference, type NameOrder, type SpeciesNameSet } from "@/lib/carousel/names";
import EditorTopBar from "./EditorTopBar";
import EditorLeftPanel from "./EditorLeftPanel";
import EditorStage, { type StageCardInput } from "./EditorStage";
import EditorBottomPanel from "./EditorBottomPanel";
import EditorThumbRail, { type ThumbRailCard } from "./EditorThumbRail";
import EditorExportModal from "./EditorExportModal";
import UpsellModal from "./UpsellModal";

export interface StudioSpecies extends SpeciesNameSet {
  /** present for imported species (eBird code); absent for user-added slides */
  code?: string;
}

export interface Entitlements {
  loggedIn: boolean;
  premium: boolean;
  handle: string | null;
}

export interface CarouselStudioProps {
  initialCover: Partial<CoverSlideData>;
  initialSpecies: StudioSpecies[];
  entitlements: Entitlements;
  source: "trip" | "public";
  /** base name for the downloaded ZIP, without extension */
  zipBaseName: string;
  /** extra properties attached to PostHog events (e.g. trip_number, date) */
  analytics?: Record<string, unknown>;
  /** Present when mounted as an in-page overlay (not its own route) — shows a "← Back" control. */
  onClose?: () => void;
}

// ── Unified slide state ──────────────────────────────────────────────
type SpeciesSlide = {
  id: string;
  enabled: boolean;
  code?: string;          // imported slides keep their eBird code (can't be removed, only disabled)
  /** Raw name set this slide was imported with — kept alongside the
   * currently-*displayed* namePrimary/Secondary/Tertiary below so the
   * language toggle can re-derive them at any time without losing data
   * (e.g. switching en → kz → en again round-trips cleanly). Absent for
   * slides added blank in the editor (nothing to derive from). */
  names?: SpeciesNameSet;
  namePrimary: string;
  nameSecondary: string;
  nameTertiary: string;
  photoUrl: string | null;
  photoOffsetY?: number;
  photoZoom?: number;
};

interface GeneratedSlide {
  name: string;
  url: string;
  blob: Blob;
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // skip broken photo, draw without it
    img.src = url;
  });
}

const CLOSING_DEFAULTS: ClosingSlideData = {
  heading: "See you in the field",
  subtext: "follow for more field diaries",
  handle: "",
};

type ActiveCardKind = "cover" | "species" | "closing";
interface EditorCard {
  key: string;
  kind: ActiveCardKind;
  photoUrl: string | null;
  photoOffsetY: number;
  photoZoom: number;
  species?: SpeciesSlide;
}

export default function CarouselStudio({
  initialCover, initialSpecies, entitlements, source, zipBaseName, analytics = {}, onClose,
}: CarouselStudioProps) {
  const [templateId, setTemplateId] = useState(DEFAULT_TEMPLATE.id);
  const [pairingId, setPairingId] = useState(DEFAULT_PAIRING.id);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const template = TEMPLATES[templateId] ?? DEFAULT_TEMPLATE;
  const pairing = FONT_PAIRINGS.find((p) => p.id === pairingId) ?? DEFAULT_PAIRING;
  // Premium designs are freely previewable; only export is gated.
  const premiumLocked = (template.premium || pairing.premium) && !entitlements.premium;

  // Watermark policy: premium users get their own handle (or nothing);
  // the trip page keeps its historical clean output; the public studio
  // always watermarks free-tier downloads.
  const watermark: SlideStyle["watermark"] = entitlements.premium
    ? (entitlements.handle ? { text: entitlements.handle } : null)
    : source === "trip" ? null : { text: "LiferRadar.com" };
  const style: SlideStyle = {
    fonts: { display: pairing.display, body: pairing.body },
    watermark,
  };

  // ── Cover state ──
  const [cover, setCover] = useState<CoverSlideData>({
    kickerText: initialCover.kickerText ?? "FIELD DIARY",
    headlineTitle: initialCover.headlineTitle ?? "",
    headlineFontSize: initialCover.headlineFontSize ?? 140,
    subtitle: initialCover.subtitle ?? "",
    stats: initialCover.stats ?? [],
    swipeText: initialCover.swipeText ?? "",
    backgroundMode: "auto",
    photoOffsetY: 0,
    photoZoom: 1,
  });
  const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | null>(null);
  const coverPhotoInputRef = useRef<HTMLInputElement>(null);

  // ── Species name order (which raw name fills the large/medium/small slot)
  // — a prior explicit choice wins (localStorage), otherwise the browser's
  // own language decides; English + Latin for everyone else, since English
  // is the one name every species actually has. ──
  const [nameOrder, setNameOrder] = useState<NameOrder>(() => detectDefaultNameOrder());

  // ── Species slides (imported + user-added, one list, reorderable) ──
  const [slides, setSlides] = useState<SpeciesSlide[]>(() =>
    initialSpecies.map((s) => {
      const names: SpeciesNameSet = { nameEn: s.nameEn, nameScientific: s.nameScientific, nameRu: s.nameRu, nameKz: s.nameKz };
      const slots = assignNameSlots(names, nameOrder);
      return {
        id: crypto.randomUUID(),
        enabled: true,
        code: s.code,
        names,
        namePrimary: slots.namePrimary ?? "",
        nameSecondary: slots.nameSecondary ?? "",
        nameTertiary: slots.nameTertiary,
        photoUrl: null,
      };
    })
  );
  const photoInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  /** Re-derives every imported slide's displayed names from its raw name set
   * — a bulk "apply this order" action, not a live binding, so manual
   * per-slide edits made afterward stick until the next change. */
  function applyNameOrder(order: NameOrder) {
    setNameOrder(order);
    saveNameOrderPreference(order);
    setSlides((prev) => prev.map((s) => {
      if (!s.names) return s;
      const slots = assignNameSlots(s.names, order);
      return { ...s, namePrimary: slots.namePrimary ?? "", nameSecondary: slots.nameSecondary ?? "", nameTertiary: slots.nameTertiary };
    }));
  }

  // ── Closing slide (pinned last, off by default) ──
  const [closingEnabled, setClosingEnabled] = useState(false);
  const [closing, setClosing] = useState<ClosingSlideData>(CLOSING_DEFAULTS);
  const [closingPhotoUrl, setClosingPhotoUrl] = useState<string | null>(null);
  const closingPhotoInputRef = useRef<HTMLInputElement>(null);

  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [generatedSlides, setGeneratedSlides] = useState<GeneratedSlide[] | null>(null);

  // ── Editor navigation ──
  const [activeIndex, setActiveIndexRaw] = useState(0);
  function goto(i: number) {
    setActiveIndexRaw(i);
  }

  // ── Shared photo image cache: loads each distinct object URL once ──
  const [photoImages, setPhotoImages] = useState<Map<string, HTMLImageElement>>(new Map());
  useEffect(() => {
    const urls = [coverPhotoUrl, ...slides.map((s) => s.photoUrl), closingPhotoUrl]
      .filter((u): u is string => !!u);
    const missing = urls.filter((u) => !photoImages.has(u));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const loaded = await Promise.all(missing.map(async (u) => [u, await loadImage(u)] as const));
      if (cancelled) return;
      setPhotoImages((prev) => {
        const next = new Map(prev);
        for (const [u, img] of loaded) if (img) next.set(u, img);
        return next;
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverPhotoUrl, slides, closingPhotoUrl]);
  const resolvePhoto = (url: string | null) => (url ? photoImages.get(url) ?? null : null);

  // Revoke result object URLs when replaced/unmounted
  useEffect(() => {
    return () => { generatedSlides?.forEach((s) => URL.revokeObjectURL(s.url)); };
  }, [generatedSlides]);

  // Revoke all photo object URLs on unmount
  useEffect(() => {
    const list = slides;
    const coverUrl = coverPhotoUrl;
    const closingUrl = closingPhotoUrl;
    return () => {
      list.forEach((s) => { if (s.photoUrl) URL.revokeObjectURL(s.photoUrl); });
      if (coverUrl) URL.revokeObjectURL(coverUrl);
      if (closingUrl) URL.revokeObjectURL(closingUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cover helpers ──
  function updateCover(patch: Partial<CoverSlideData>) {
    setCover((prev) => ({ ...prev, ...patch }));
  }
  function handleCoverPhotoUpload(file: File) {
    setCoverPhotoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }
  function removeCoverPhoto() {
    setCoverPhotoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }

  // ── Species slide helpers ──
  function updateSlide(id: string, patch: Partial<Omit<SpeciesSlide, "id">>) {
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }
  function toggleSlide(id: string) {
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
  }
  function setAllSlidesEnabled(enabled: boolean) {
    setSlides((prev) => prev.map((s) => ({ ...s, enabled })));
  }
  function addSlide() {
    const id = crypto.randomUUID();
    setSlides((prev) => [...prev, {
      id, enabled: true,
      namePrimary: "", nameSecondary: "", nameTertiary: "", photoUrl: null,
    }]);
    setActiveIndexRaw(1 + slides.filter((s) => s.enabled).length);
  }
  function removeSlide(id: string) {
    setSlides((prev) => {
      const slide = prev.find((s) => s.id === id);
      if (slide?.photoUrl) URL.revokeObjectURL(slide.photoUrl);
      return prev.filter((s) => s.id !== id);
    });
    goto(0);
  }
  /** Drag-and-drop reorder — moves `fromId` to sit just before `toId`. */
  function reorderSlides(fromId: string, toId: string) {
    if (fromId === toId) return;
    setSlides((prev) => {
      const fromIdx = prev.findIndex((s) => s.id === fromId);
      if (fromIdx === -1 || prev.findIndex((s) => s.id === toId) === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      const insertAt = next.findIndex((s) => s.id === toId);
      next.splice(insertAt, 0, moved);
      return next;
    });
  }
  function handleSlidePhotoUpload(id: string, file: File) {
    setSlides((prev) => prev.map((s) => {
      if (s.id !== id) return s;
      if (s.photoUrl) URL.revokeObjectURL(s.photoUrl);
      return { ...s, photoUrl: URL.createObjectURL(file) };
    }));
  }
  function removeSlidePhoto(id: string) {
    setSlides((prev) => prev.map((s) => {
      if (s.id !== id) return s;
      if (s.photoUrl) URL.revokeObjectURL(s.photoUrl);
      return { ...s, photoUrl: null };
    }));
  }

  const enabledCount = slides.filter((s) => s.enabled).length;
  function updateClosing(patch: Partial<ClosingSlideData>) {
    setClosing((prev) => ({ ...prev, ...patch }));
  }
  function handleClosingPhotoUpload(file: File) {
    setClosingPhotoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }
  function removeClosingPhoto() {
    setClosingPhotoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }
  function toggleClosing() {
    setClosingEnabled((v) => {
      const next = !v;
      if (next) setActiveIndexRaw(1 + enabledCount);
      else goto(0);
      return next;
    });
  }

  function openPremiumUpsell(trigger: string) {
    setUpsellOpen(true);
    posthog.capture("carousel_upsell_shown", { source, trigger, template: template.id, pairing: pairing.id });
  }

  // ── Unified card list driving the stage / thumb rail / bottom panel ──
  const enabledSlides = slides.filter((s) => s.enabled);
  const editorCards: EditorCard[] = [
    { key: "cover", kind: "cover", photoUrl: coverPhotoUrl, photoOffsetY: cover.photoOffsetY ?? 0, photoZoom: cover.photoZoom ?? 1 },
    ...enabledSlides.map((s): EditorCard => ({
      key: s.id, kind: "species", photoUrl: s.photoUrl, photoOffsetY: s.photoOffsetY ?? 0, photoZoom: s.photoZoom ?? 1, species: s,
    })),
    ...(closingEnabled ? [{
      key: "closing", kind: "closing" as const, photoUrl: closingPhotoUrl,
      photoOffsetY: closing.photoOffsetY ?? 0, photoZoom: closing.photoZoom ?? 1,
    }] : []),
  ];
  const idx = Math.min(activeIndex, editorCards.length - 1);
  const active = editorCards[idx];

  const stageCards: StageCardInput[] = editorCards.map((c) => {
    if (c.kind === "cover") {
      const data: CoverSlideData = { ...cover, photo: resolvePhoto(coverPhotoUrl) };
      return { key: c.key, kind: "cover", data, photoUrl: c.photoUrl };
    }
    if (c.kind === "species") {
      const s = c.species!;
      const data: SpeciesSlideData = {
        namePrimary: s.namePrimary || null, nameSecondary: s.nameSecondary || null, nameTertiary: s.nameTertiary,
        photo: resolvePhoto(s.photoUrl), photoOffsetY: s.photoOffsetY, photoZoom: s.photoZoom,
      };
      return { key: c.key, kind: "species", data, photoUrl: c.photoUrl };
    }
    const data: ClosingSlideData = { ...closing, photo: resolvePhoto(closingPhotoUrl) };
    return { key: c.key, kind: "closing", data, photoUrl: c.photoUrl };
  });
  const thumbCards: ThumbRailCard[] = editorCards.map((c) => ({ key: c.key, kind: c.kind, photoUrl: c.photoUrl }));

  // ── Photo control dispatch for whichever slide is active ──
  function onPhotoOffsetYChange(v: number) {
    if (active.kind === "cover") updateCover({ photoOffsetY: v });
    else if (active.kind === "species") updateSlide(active.species!.id, { photoOffsetY: v });
    else updateClosing({ photoOffsetY: v });
  }
  function onPhotoZoomChange(v: number) {
    if (active.kind === "cover") updateCover({ photoZoom: v });
    else if (active.kind === "species") updateSlide(active.species!.id, { photoZoom: v });
    else updateClosing({ photoZoom: v });
  }
  function onChangePhotoClick() {
    if (active.kind === "cover") coverPhotoInputRef.current?.click();
    else if (active.kind === "species") photoInputRefs.current.get(active.species!.id)?.click();
    else closingPhotoInputRef.current?.click();
  }
  function onRemovePhotoClick() {
    if (active.kind === "cover") removeCoverPhoto();
    else if (active.kind === "species") removeSlidePhoto(active.species!.id);
    else removeClosingPhoto();
  }
  function updateActiveSpecies(patch: Partial<Omit<SpeciesSlide, "id" | "code">>) {
    if (active.kind === "species") updateSlide(active.species!.id, patch);
  }

  async function renderSlideToBlob(
    kind: "cover" | "species" | "closing",
    data: CoverSlideData | SpeciesSlideData | ClosingSlideData
  ): Promise<Blob> {
    const canvas = document.createElement("canvas");
    canvas.width = SLIDE_W;
    canvas.height = SLIDE_H;
    const ctx = canvas.getContext("2d")!;
    if (kind === "cover") template.drawCover(ctx, data as CoverSlideData, style);
    else if (kind === "species") template.drawSpecies(ctx, data as SpeciesSlideData, style);
    else template.drawClosing(ctx, data as ClosingSlideData, style);
    return canvasToPngBlob(canvas);
  }

  // ── Generate ──
  async function handleGenerate() {
    if (generating || enabledCount === 0) return;
    if (premiumLocked) {
      openPremiumUpsell("premium_design");
      return;
    }
    setGenerating(true);
    try {
      await ensureFontsLoaded(pairing);

      const out: GeneratedSlide[] = [];

      const coverPhoto = coverPhotoUrl ? await loadImage(coverPhotoUrl) : null;
      const coverBlob = await renderSlideToBlob("cover", { ...cover, photo: coverPhoto });
      out.push({ name: "00-cover.png", url: URL.createObjectURL(coverBlob), blob: coverBlob });

      let i = 1;
      for (const s of slides) {
        if (!s.enabled) continue;
        const photo = s.photoUrl ? await loadImage(s.photoUrl) : null;
        const blob = await renderSlideToBlob("species", {
          namePrimary: s.namePrimary || null,
          nameSecondary: s.nameSecondary || null,
          nameTertiary: s.nameTertiary,
          photo, photoOffsetY: s.photoOffsetY, photoZoom: s.photoZoom,
        });
        const slug = s.code ?? s.id.slice(0, 6);
        out.push({ name: `${String(i).padStart(2, "0")}-${slug}.png`, url: URL.createObjectURL(blob), blob });
        i++;
      }

      if (closingEnabled) {
        const photo = closingPhotoUrl ? await loadImage(closingPhotoUrl) : null;
        const blob = await renderSlideToBlob("closing", { ...closing, photo });
        out.push({ name: `${String(i).padStart(2, "0")}-closing.png`, url: URL.createObjectURL(blob), blob });
      }

      setGeneratedSlides(out);
      posthog.capture("carousel_generated", {
        ...analytics, source, template: template.id, pairing: pairing.id,
        watermarked: !!style.watermark, slides: out.length, species_count: enabledCount,
      });
    } catch {
      setToast("Generation failed — try again");
      setTimeout(() => setToast(null), 2500);
    } finally {
      setGenerating(false);
    }
  }

  function openExport() {
    if (premiumLocked) {
      openPremiumUpsell("premium_design");
      return;
    }
    setExportOpen(true);
    setGeneratedSlides(null);
    void handleGenerate();
  }

  async function handleDownloadAll() {
    if (!generatedSlides || downloading) return;
    setDownloading(true);
    try {
      const zip = new JSZip();
      for (const slide of generatedSlides) zip.file(slide.name, slide.blob);
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${zipBaseName}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);

      posthog.capture("carousel_downloaded", {
        ...analytics, source, template: template.id, pairing: pairing.id,
        watermarked: !!style.watermark, slides: generatedSlides.length,
      });
      if (style.watermark?.text === "LiferRadar.com") {
        setToast(`Downloaded ${generatedSlides.length} slides — Premium swaps the watermark for your own @handle`);
        posthog.capture("carousel_upsell_shown", { source, trigger: "post_download_toast" });
        setTimeout(() => setToast(null), 5000);
      } else {
        setToast(`Downloaded ${generatedSlides.length} slides`);
        setTimeout(() => setToast(null), 2500);
      }
    } catch {
      setToast("Download failed — try again");
      setTimeout(() => setToast(null), 2500);
    } finally {
      setDownloading(false);
    }
  }

  const watermarkNote = watermark?.text === "LiferRadar.com" ? "Remove the LiferRadar.com watermark" : null;

  return (
    <div className="ced-shell">
      <EditorTopBar
        source={source}
        totalSlides={editorCards.length}
        onExportClick={openExport}
        exportDisabled={enabledCount === 0}
        onClose={onClose}
      />

      <div className="ced-editor">
        <EditorLeftPanel
          slides={slides}
          toggleSlide={toggleSlide}
          setAllSlidesEnabled={setAllSlidesEnabled}
          reorderSlides={reorderSlides}
          templateId={templateId}
          setTemplateId={setTemplateId}
          premiumLocked={premiumLocked}
          onPremiumSwatchClick={() => openPremiumUpsell("premium_design")}
          nameOrder={nameOrder}
          onApplyNameOrder={applyNameOrder}
          hasRegionalNames={slides.some((s) => s.names?.nameRu || s.names?.nameKz)}
        />

        <div className="ced-main">
          <EditorStage
            cards={stageCards}
            activeIndex={idx}
            setActiveIndex={goto}
            template={template}
            style={style}
            onChangePhotoClick={onChangePhotoClick}
            onRemoveActive={
              active.kind === "species" && active.species && !active.species.code
                ? () => removeSlide(active.species!.id)
                : active.kind === "closing"
                  ? () => setClosingEnabled(false)
                  : undefined
            }
            onUpdateActiveCover={updateCover}
            onUpdateActiveSpecies={updateActiveSpecies}
            onUpdateActiveClosing={updateClosing}
          />
          <EditorThumbRail
            cards={thumbCards}
            activeIndex={idx}
            setActiveIndex={goto}
            onAddSpeciesSlide={addSlide}
            closingEnabled={closingEnabled}
            onToggleClosing={toggleClosing}
            reorderSlides={reorderSlides}
          />
        </div>

        <div className="ced-bottom-panel">
          <EditorBottomPanel
            activeKind={active.kind}
            photoUrl={active.photoUrl}
            photoOffsetY={active.photoOffsetY}
            photoZoom={active.photoZoom}
            onPhotoOffsetYChange={onPhotoOffsetYChange}
            onPhotoZoomChange={onPhotoZoomChange}
            onChangePhotoClick={onChangePhotoClick}
            onRemovePhotoClick={onRemovePhotoClick}
            pairingId={pairingId}
            setPairingId={setPairingId}
            premiumLocked={premiumLocked}
            onPremiumSwatchClick={() => openPremiumUpsell("premium_design")}
            cover={cover}
            updateCover={updateCover}
            activeSpecies={active.species ? {
              code: active.species.code,
              namePrimary: active.species.namePrimary,
              nameSecondary: active.species.nameSecondary,
              nameTertiary: active.species.nameTertiary,
            } : null}
            updateActiveSpecies={updateActiveSpecies}
            closing={closing}
            updateClosing={updateClosing}
          />
        </div>
      </div>

      <input
        ref={coverPhotoInputRef}
        type="file" accept="image/*" className="car-photo-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCoverPhotoUpload(f); }}
      />
      <input
        ref={closingPhotoInputRef}
        type="file" accept="image/*" className="car-photo-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleClosingPhotoUpload(f); }}
      />
      {slides.map((s) => (
        <input
          key={s.id}
          ref={(el) => { if (el) photoInputRefs.current.set(s.id, el); else photoInputRefs.current.delete(s.id); }}
          type="file" accept="image/*" className="car-photo-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSlidePhotoUpload(s.id, f); }}
        />
      ))}

      {toast && <div className="car-toast">{toast}</div>}

      <EditorExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        generating={generating}
        generatedSlides={generatedSlides}
        downloading={downloading}
        onDownloadAll={handleDownloadAll}
        enabledCount={enabledCount}
        watermarked={!!watermarkNote}
        onWatermarkNoteClick={() => openPremiumUpsell("remove_watermark")}
      />

      <UpsellModal
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        loggedIn={entitlements.loggedIn}
        source={source}
      />
    </div>
  );
}
